/**
 * emit.ts — low-level event emission utilities for the admin client.
 * Uses Supabase RPC emit_event / emit_pipeline.
 *
 * VITE_E2E_MODE: when true, emitted events get `e2e-` prefix on correlation_id
 * so the mock worker (LLM_PROVIDER=mock, include='e2e-') picks them up while
 * the primary worker (exclude='e2e-') ignores them. Caller code is unaware.
 */
import { v4 as uuidv4 } from 'uuid';
import { getSupabase } from '@/auth/supabase-client';
import { env } from '@/lib/env';
import type { Database, Json } from '@wandero/shared';

export type EventRow = Database['public']['Tables']['events']['Row'];

export interface EmitOptions {
  correlation_id?: string;
  causation_id?: number;
  process_after?: Date | string;
  event_id?: string;
  max_attempts?: number;
}

/** Prefix correlation_id with `e2e-` when running under E2E.
 *  - undefined in E2E → generate a fresh `e2e-<uuid>` so mock worker can claim it.
 *  - undefined out of E2E → leave undefined (RPC will null it out).
 *  - already starts with `e2e-` → leave as-is. */
function applyE2ENamespace(id: string | undefined): string | undefined {
  if (!env.VITE_E2E_MODE) return id;
  if (id === undefined) return `e2e-${uuidv4()}`;
  if (id.startsWith('e2e-')) return id;
  return `e2e-${id}`;
}

export async function emitEvent(
  type: string,
  payload: Record<string, unknown> = {},
  opts: EmitOptions = {},
): Promise<EventRow> {
  const sb = getSupabase();
  const correlationId = applyE2ENamespace(opts.correlation_id);
  const { data, error } = await sb.rpc('emit_event', {
    p_type: type,
    p_payload: payload as Json,
    ...(correlationId !== undefined ? { p_correlation_id: correlationId } : {}),
    ...(opts.causation_id !== undefined ? { p_causation_id: opts.causation_id } : {}),
    ...(opts.process_after !== undefined
      ? {
          p_process_after:
            typeof opts.process_after === 'string'
              ? opts.process_after
              : opts.process_after.toISOString(),
        }
      : {}),
    ...(opts.event_id !== undefined ? { p_event_id: opts.event_id } : {}),
    ...(opts.max_attempts !== undefined ? { p_max_attempts: opts.max_attempts } : {}),
  });
  if (error) throw error;
  return data as EventRow;
}

export interface PipelineStep {
  type: string;
  payload?: Record<string, unknown>;
  depends_on_index?: number;
  correlation_id?: string;
  event_id?: string;
  max_attempts?: number;
}

export async function emitPipeline(steps: PipelineStep[]): Promise<EventRow[]> {
  const sb = getSupabase();
  const namespaced = steps.map((s) => {
    const cid = applyE2ENamespace(s.correlation_id);
    return cid !== undefined ? { ...s, correlation_id: cid } : s;
  });
  const { data, error } = await sb.rpc('emit_pipeline', {
    p_steps: namespaced as unknown as Json,
  });
  if (error) throw error;
  return data as EventRow[];
}
